import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpEventType } from '@angular/common/http';

import { Call4uAudioService, DtmfAction } from 'src/app/shared/services/call4u-audio.service';
import { LeadList } from 'src/app/models/LeadList';
import { ListsService } from 'src/app/shared/services/lists.service';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { DashboardService } from 'src/app/shared/services/dashboard.service';
import { WalletService } from 'src/app/shared/services/wallet.service';
import { WalletBalance } from 'src/app/models/Wallet';

interface DtmfActionRow {
  dtmf_key: string;
  action_type: string;
  action_text: string;
  action_list_id: string;
}

@Component({
  selector: 'app-call4u-audio-create',
  templateUrl: './call4u-audio-create.component.html',
  styleUrls: ['./call4u-audio-create.component.scss']
})
export class Call4uAudioCreateComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];

  // Campos do formulário principal
  title = '';
  text = '';

  // Upload
  selectedFile: File | null = null;
  fileName = '';
  fileDurationError = false;
  private readonly MAX_FILE_SIZE = 10 * 1024 * 1024;
  private readonly MAX_AUDIO_DURATION = 30;

  // TTS Modal - Steps
  ttsStep = 1;
  ttsTitle = '';
  ttsText = '';
  ttsVoiceId = '';
  ttsSaving = false;

  // TTS Preview
  ttsPreviewAudio: HTMLAudioElement | null = null;
  ttsPreviewPlaying = false;
  ttsPreviewLoading = false;
  ttsPreviewUrl: string | null = null;
  ttsPreviewDuration: number | null = null;

  // Ações DTMF
  dtmfActions: DtmfActionRow[] = [];

  // Estado
  saving = false;
  uploading = false;
  uploadProgress: number | null = null;

  // Listas do SMS Funnel
  lists: LeadList[] = [];

  // Créditos de ligação do pacote (gate do TTS para quem paga a ligação pelo pacote)
  callCredits = 0;
  /** A ligação desta conta é cobrada pela carteira (`/wallet/billing`). */
  callBilledByWallet = false;
  private walletBalance: WalletBalance | null = null;

  // Opções de dígitos DTMF
  dtmfKeys = [
    { key: 'all', label: 'Todos (0-9, * e #)' },
    { key: '11', label: 'Não atendido' },
    { key: '10', label: 'Nenhuma opção' },
    { key: '0', label: '0' },
    { key: '1', label: '1' },
    { key: '2', label: '2' },
    { key: '3', label: '3' },
    { key: '4', label: '4' },
    { key: '5', label: '5' },
    { key: '6', label: '6' },
    { key: '7', label: '7' },
    { key: '8', label: '8' },
    { key: '9', label: '9' },
    { key: '12', label: '*' },
    { key: '13', label: '#' },
  ];

  // Chaves individuais expandidas de "Todos"
  private readonly allKeys = ['0','1','2','3','4','5','6','7','8','9','12','13'];

  // Opções de ações
  actionTypes = [
    { value: 'postback_sms_funnel', label: 'Postback' },
    { value: 'add_to_list', label: 'Lista' },
  ];

  // Vozes disponíveis (Eleven Labs)
  voices = [
    { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Sarah - Feminino (Suave)' },
    { id: 'JBFqnCBsd6RMkjVDRZzb', name: 'George - Masculino (Formal)' },
    { id: 'TX3LPaxmHKxFdv7VOQHJ', name: 'Liam - Masculino (Articulado)' },
    { id: 'XB0fDUnXU5powFXDhCwa', name: 'Charlotte - Feminino (Jovem)' },
    { id: 'Xb7hH8MSUJpSbSDYk0k2', name: 'Alice - Feminino (Confiante)' },
    { id: 'bIHbv24MWmeRgasZH58o', name: 'Will - Masculino (Amigável)' },
    { id: 'iP95p4xoKVk53GoZ742B', name: 'Chris - Masculino (Casual)' },
    { id: 'nPczCjzI2devNBz1zQrb', name: 'Brian - Masculino (Narrativo)' },
    { id: 'onwK4e9ZLuTAKqWW03F9', name: 'Daniel - Masculino (Britânico)' },
    { id: 'pFZP5JQG7iQjIQuC4Bku', name: 'Lily - Feminino (Britânico)' },
  ];

  constructor(
    private audioService: Call4uAudioService,
    private listsService: ListsService,
    private toastService: ToastService,
    private router: Router,
    private modalService: NgbModal,
    private dashboardService: DashboardService,
    private walletService: WalletService
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.listsService.listAll().subscribe(lists => this.lists = lists)
    );
    // Saldo vivo: o gate deve refletir o crédito atual (mesma checagem que o
    // backend faz em store/previewTts), e não o snapshot do /login em cache —
    // senão uma recarga confirmada só passa a valer após deslogar/logar.
    this.subscriptions.push(
      forkJoin({
        credits: this.dashboardService.userCreditsInfo().pipe(catchError(() => of(null))),
        billing: this.walletService.getBilling().pipe(catchError(() => of(null))),
        balance: this.walletService.getBalance().pipe(catchError(() => of(null))),
      }).subscribe(({ credits, billing, balance }) => {
        this.callCredits = credits?.call_credits ?? 0;
        this.callBilledByWallet = billing?.call === 'wallet';
        this.walletBalance = balance;
      })
    );
  }

  /**
   * Mesma porta do backend (`WalletCallCharge::passesMinimumBalanceGate` em store/previewTts):
   * quem paga ligação pela carteira (T.9: o pacote foi zerado na migração) depende do saldo em
   * R$ — sem resposta do saldo não trava, quem decide é o backend; quem está no pacote depende
   * dos créditos de ligação.
   */
  get canUseTts(): boolean {
    if (this.callBilledByWallet) {
      return !this.walletBalance || this.walletBalance.overdraft || this.walletBalance.available_micros > 0;
    }

    return this.callCredits > 0;
  }

  ngOnDestroy(): void {
    this.stopTtsPreview();
    this.cleanupPreviewUrl();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // Upload
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (file.size > this.MAX_FILE_SIZE) {
        this.toastService.error('O arquivo excede o tamanho máximo de 10MB');
        input.value = '';
        return;
      }
      this.fileDurationError = false;
      this.selectedFile = file;
      this.fileName = file.name;

      // Validar duração do áudio no client
      const url = URL.createObjectURL(file);
      const audio = new Audio(url);
      audio.addEventListener('loadedmetadata', () => {
        if (isFinite(audio.duration) && audio.duration > this.MAX_AUDIO_DURATION) {
          this.fileDurationError = true;
          this.toastService.error(`O áudio tem ${Math.ceil(audio.duration)}s e excede o limite de ${this.MAX_AUDIO_DURATION}s`);
        }
        URL.revokeObjectURL(url);
      }, { once: true });
    }
  }

  removeFile(): void {
    this.selectedFile = null;
    this.fileName = '';
    this.fileDurationError = false;
  }

  // TTS Modal
  openTtsModal(modal: any, modalNoCredits: any): void {
    if (!this.canUseTts) {
      this.modalService.open(modalNoCredits, { centered: true });
      return;
    }
    this.ttsStep = 1;
    this.ttsTitle = this.title || '';
    this.ttsText = this.text || '';
    this.ttsVoiceId = this.voices[0]?.id || '';
    this.ttsSaving = false;
    this.ttsPreviewLoading = false;
    this.ttsPreviewDuration = null;
    this.stopTtsPreview();
    this.cleanupPreviewUrl();
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  nextStep(): void {
    if (this.ttsStep < 3) {
      this.ttsStep++;
    }
  }

  prevStep(): void {
    if (this.ttsStep > 1) {
      // Se voltar do step 3, limpar preview
      if (this.ttsStep === 3) {
        this.stopTtsPreview();
        this.cleanupPreviewUrl();
        this.ttsPreviewDuration = null;
      }
      this.ttsStep--;
    }
  }

  getVoiceName(voiceId: string): string {
    const voice = this.voices.find(v => v.id === voiceId);
    return voice ? voice.name : voiceId;
  }

  // TTS Preview
  generatePreview(): void {
    if (this.ttsPreviewLoading) return;

    this.ttsPreviewLoading = true;
    this.stopTtsPreview();
    this.cleanupPreviewUrl();
    this.ttsPreviewDuration = null;

    this.subscriptions.push(
      this.audioService.previewTTS(this.ttsText, this.ttsVoiceId)
        .pipe(finalize(() => this.ttsPreviewLoading = false))
        .subscribe({
          next: (blob: Blob) => {
            this.ttsPreviewUrl = URL.createObjectURL(blob);
            this.ttsPreviewAudio = new Audio(this.ttsPreviewUrl);

            // Obter duração quando metadata carregar
            this.ttsPreviewAudio.addEventListener('loadedmetadata', () => {
              if (this.ttsPreviewAudio && isFinite(this.ttsPreviewAudio.duration)) {
                this.ttsPreviewDuration = Math.ceil(this.ttsPreviewAudio.duration);
              }
            }, { once: true });

            this.ttsPreviewAudio.addEventListener('ended', () => {
              this.ttsPreviewPlaying = false;
            }, { once: true });

            this.toastService.success('Preview gerado com sucesso!');
          },
          error: (err) => {
            this.toastService.error(err.error?.message || 'Erro ao gerar preview do áudio');
          }
        })
    );
  }

  toggleTtsPreview(): void {
    if (!this.ttsPreviewAudio) return;

    if (this.ttsPreviewPlaying) {
      this.ttsPreviewAudio.pause();
      this.ttsPreviewPlaying = false;
    } else {
      this.ttsPreviewAudio.play().then(() => {
        this.ttsPreviewPlaying = true;
      }).catch(() => {
        this.toastService.error('Erro ao reproduzir preview');
      });
    }
  }

  stopTtsPreview(): void {
    if (this.ttsPreviewAudio) {
      this.ttsPreviewAudio.pause();
      this.ttsPreviewAudio = null;
    }
    this.ttsPreviewPlaying = false;
  }

  private cleanupPreviewUrl(): void {
    if (this.ttsPreviewUrl) {
      URL.revokeObjectURL(this.ttsPreviewUrl);
      this.ttsPreviewUrl = null;
    }
  }

  formatPreviewDuration(seconds: number | null): string {
    if (seconds === null) return '--:--';
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  }

  get isTtsStep1Valid(): boolean {
    return this.ttsTitle.trim().length > 0 && this.ttsText.trim().length > 0;
  }

  get isTtsStep2Valid(): boolean {
    return this.ttsVoiceId.length > 0;
  }

  get isTtsOverDuration(): boolean {
    return this.ttsPreviewDuration !== null && this.ttsPreviewDuration > this.MAX_AUDIO_DURATION;
  }

  saveTts(modal: any): void {
    if (this.ttsSaving) return;
    if (this.isTtsOverDuration) {
      this.toastService.error(`O áudio tem ${this.ttsPreviewDuration}s e excede o limite de ${this.MAX_AUDIO_DURATION}s. Reduza o texto.`);
      return;
    }
    this.ttsSaving = true;

    const actions = this.buildActions();

    this.subscriptions.push(
      this.audioService.generateTTS(this.ttsTitle, this.ttsText, actions, this.ttsVoiceId)
        .pipe(finalize(() => this.ttsSaving = false))
        .subscribe({
          next: () => {
            this.toastService.success('Áudio TTS gerado com sucesso!');
            modal.close();
            this.router.navigate(['/call4u-audios']);
          },
          error: (err) => this.toastService.error(err.error?.message || 'Erro ao gerar áudio TTS')
        })
    );
  }

  // DTMF Actions
  addAction(): void {
    this.dtmfActions.push({ dtmf_key: '', action_type: '', action_text: '', action_list_id: '' });
  }

  removeAction(index: number): void {
    this.dtmfActions.splice(index, 1);
  }

  getAvailableKeys(currentIndex: number): { key: string; label: string }[] {
    const otherActions = this.dtmfActions.filter((_, i) => i !== currentIndex);
    const usedKeys = otherActions.map(a => a.dtmf_key);

    // Se outro row usa "all", bloqueia as teclas individuais (0-9, *, #)
    const allUsed = usedKeys.includes('all');

    return this.dtmfKeys.filter(k => {
      if (usedKeys.includes(k.key)) return false;
      if (allUsed && this.allKeys.includes(k.key)) return false;
      // Se alguma tecla individual está usada, bloqueia "all"
      if (k.key === 'all' && usedKeys.some(uk => this.allKeys.includes(uk))) return false;
      return true;
    });
  }

  needsTextParam(actionType: string): boolean {
    return ['postback_sms_funnel'].includes(actionType);
  }

  needsListParam(actionType: string): boolean {
    return actionType === 'add_to_list';
  }

  getParamPlaceholder(actionType: string): string {
    const placeholders: Record<string, string> = {
      'postback_sms_funnel': 'Digite a url aqui',
    };
    return placeholders[actionType] || '';
  }

  getParamLabel(actionType: string): string {
    const labels: Record<string, string> = {
      'postback_sms_funnel': 'Url',
    };
    return labels[actionType] || 'Parâmetro';
  }

  // Validação formulário principal (upload)
  get isUploadFormValid(): boolean {
    if (!this.title.trim()) return false;
    if (!this.selectedFile) return false;
    if (this.fileDurationError) return false;
    for (const action of this.dtmfActions) {
      if (!action.dtmf_key || !action.action_type) return false;
      if (this.needsTextParam(action.action_type) && !action.action_text.trim()) return false;
      if (this.needsListParam(action.action_type) && !action.action_list_id) return false;
    }
    return true;
  }

  // Salvar upload
  saveUpload(): void {
    if (!this.isUploadFormValid || this.saving) return;
    this.saving = true;
    this.uploading = true;
    this.uploadProgress = 0;

    const actions = this.buildActions();

    this.subscriptions.push(
      this.audioService.upload(this.selectedFile!, this.title, this.text, actions)
        .pipe(finalize(() => {
          this.saving = false;
          this.uploading = false;
          this.uploadProgress = null;
        }))
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.UploadProgress) {
              this.uploadProgress = event.total
                ? Math.round((100 * event.loaded) / event.total)
                : 0;
            } else if (event.type === HttpEventType.Response) {
              this.toastService.success('Áudio enviado com sucesso!');
              this.router.navigate(['/call4u-audios']);
            }
          },
          error: (err) => this.toastService.error(err.error?.message || 'Erro ao enviar áudio')
        })
    );
  }

  private buildActions(): DtmfAction[] {
    const result: DtmfAction[] = [];

    for (const a of this.dtmfActions) {
      const keys = a.dtmf_key === 'all' ? this.allKeys : [a.dtmf_key];

      for (const key of keys) {
        const action: DtmfAction = { dtmf_key: key, action_type: a.action_type };

        if (this.needsTextParam(a.action_type) && a.action_text) {
          const paramKey = this.getParamKey(a.action_type);
          action.action_params = { [paramKey]: a.action_text };
        } else if (this.needsListParam(a.action_type) && a.action_list_id) {
          action.action_params = { list_id: a.action_list_id };
        }

        result.push(action);
      }
    }

    return result;
  }

  private getParamKey(actionType: string): string {
    const keys: Record<string, string> = {
      'postback_sms_funnel': 'url',
    };
    return keys[actionType] || 'value';
  }
}
